import { Alert, Descriptions, Form, Input, Modal, Space } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { REASON_MAX, reverseReceipt, type Receipt } from './cashdeskApi'

interface ReceiptReverseModalProps {
  receipt: Receipt | null
  onClose: () => void
  onReversed: (reversal: Receipt) => void
}

export function ReceiptReverseModal({ receipt, onClose, onReversed }: ReceiptReverseModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ reason: string }>()
  const queryClient = useQueryClient()

  const reverse = useMutation({
    mutationFn: (input: { id: string; reason: string }) => reverseReceipt(input.id, input.reason),
    onSuccess: (reversal) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
      form.resetFields()
      onReversed(reversal)
    },
  })

  const close = () => {
    form.resetFields()
    reverse.reset()
    onClose()
  }

  return (
    <Modal
      open={receipt !== null}
      title={t('cashdesk.reverse.title')}
      okText={t('cashdesk.reverse.confirm')}
      cancelText={t('common.cancel')}
      okButtonProps={{ danger: true }}
      confirmLoading={reverse.isPending}
      onCancel={close}
      destroyOnHidden
      onOk={() =>
        submitOnValid(form, (values) => {
          if (receipt === null) return
          reverse.mutate({ id: receipt.id, reason: values.reason.trim() })
        })
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="warning"
          showIcon
          message={t('cashdesk.reverse.warnTitle')}
          description={t('cashdesk.reverse.warnText')}
        />

        {receipt ? (
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label={t('cashdesk.receipt.number')}>{receipt.receiptNo}</Descriptions.Item>
            <Descriptions.Item label={t('cashdesk.receipt.login')}>{receipt.customer.login}</Descriptions.Item>
            <Descriptions.Item label={t('cashdesk.receipt.total')}>
              {formatMoneyValue(receipt.amount)}
            </Descriptions.Item>
            <Descriptions.Item label={t('cashdesk.receipt.date')}>
              {formatDateTime(receipt.createdAt)}
            </Descriptions.Item>
          </Descriptions>
        ) : null}

        {reverse.isError ? (
          <Alert type="error" showIcon message={translateError(reverse.error, t)} />
        ) : null}

        <Form form={form} layout="vertical" requiredMark preserve={false}>
          <Form.Item
            name="reason"
            label={t('actions.reason')}
            extra={t('cashdesk.reverse.reasonHint')}
            rules={[
              { required: true, whitespace: true, message: t('cashdesk.reverse.reasonRequired') },
              { max: REASON_MAX, message: t('cashdesk.reasonTooLong') },
            ]}
          >
            <Input.TextArea rows={3} maxLength={REASON_MAX} showCount autoFocus />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  )
}
